"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.eacEvaluationSchema = exports.eacModeResultSchema = exports.EAC_YELLOW_FLAGS = exports.EAC_RED_FLAGS = exports.EAC_REASONS = exports.EAC_DEFAULT_CHAIN = exports.EAC_MODES = void 0;
exports.usableR = usableR;
exports.evaluateEac = evaluateEac;
exports.earnedSchedule = earnedSchedule;
exports.forecastFinish = forecastFinish;
exports.walkEtc = walkEtc;
exports.walkWorkdays = walkWorkdays;
exports.eacWire = eacWire;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · THE FIVE ESTIMATES AT COMPLETION — decisions.md EV-1…EV-7.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The source is *Forecastify EAC és EVM mezők – összefoglaló a fejlesztőknek*. Every EAC is
 * `AC + ETC`; only the ETC's assumption differs.
 *
 * | Mode       | HU                        | ETC                              |
 * | ---------- | ------------------------- | -------------------------------- |
 * | `budget`   | Alaprátás (tervtartó)     | BAC − EV                         |
 * | `trend`    | Trendtartó                | (BAC − EV) / CPI                 |
 * | `schedule` | Ütemkorrigált             | (BAC − EV) / (CPI × SPI(t))      |
 * | `resource` | Erőforrás-alapú           | Σ remaining allocation (REM)     |
 * | `adjusted` | Korrigált erőforrás-alapú | REM × r                          |
 *
 * ⚠️⚠️ **THE RESOURCE BRANCH IS NEVER DIVIDED BY CPI.** An allocation is already in logged-hour
 * shape; CPI converts estimated hours into logged ones. Only r — what these people actually log
 * against what they are allocated — adjusts it.
 *
 * ⚠️ **The server builds the facts and runs this**, so it can also date each mode's finish on the
 * workspace's own calendar; the screens and the agent read the evaluation it sends. Pure, so it is
 * tested with literal facts.
 */
exports.EAC_MODES = ['budget', 'trend', 'schedule', 'resource', 'adjusted'];
/** The default is the first ENABLED of these. `schedule` is never a default — it is a ceiling. */
exports.EAC_DEFAULT_CHAIN = ['adjusted', 'resource', 'trend', 'budget'];
/** Why a mode is disabled — each one says what would lift it, on the screen. */
exports.EAC_REASONS = [
    /* trend */
    'notStable', //          EV / BAC < 30 %: the CPI has not settled
    'unestimated', //        hours on unestimated tasks > 25 % of AC: CPI measures missing estimates
    'noCost', //             AC = 0: nothing to divide by
    'singleItemOverPlan', // one line item in scope, PLAN / BAC > 1.5: its routing is suspect
    /* schedule */
    'trendDisabled',
    'planThin', //           the planned value fell back to elapsed time
    'itemOverPlan', //       some line item has PLAN / BAC > 1.5
    /* resource */
    'allocationEnded', //    REM = 0 while work remains
    'underAllocated', //     REM < 50 % of the hours the remaining work needs
    'noSchedule', //         a planned person has no work schedule
    'noCostRate', //         money only: a planned person has no cost rate
    /* adjusted */
    'resourceDisabled',
    'noHistory', //          under 4 weeks or 40 allocated hours of history for these people
    'rOutOfRange', //        their r would fall outside 0.5–1.5: the history is distorted
];
/** Gaps that make every EAC unreliable — the red band. The calculation still runs. */
exports.EAC_RED_FLAGS = [
    'unestimatedHours', //   > 5 % of AC on tasks with no estimate
    'unboundHours', //       > 5 % of AC on no line item
    'itemsWithoutTasks', //  a line item with logged hours and no tasks: its EV is 0
    'reestimatedClosed', //  a closed task re-estimated since: EV moved with no work
    'rulesOnStatus', //      a routing rule on status or assignee: AC wanders between items
];
/** Notes that disable nothing — the yellow ones. EV-1 and EV-5 removed four of the spec's. */
exports.EAC_YELLOW_FLAGS = [
    'staleAllocation', //    > 20 % of REM on people who logged nothing here for 2 weeks
    'itemOverPlan', //       some line item has PLAN / BAC > 1.5
    'silentPeople', //       allocated 3+ working days of the last two weeks, logged nothing
    'fxMissing', //          money only: a rate missing on some worklog's day
];
const EPSILON = 0.004;
const STABLE_SHARE = 0.3;
const UNESTIMATED_DISABLE = 0.25;
const RED_SHARE = 0.05;
const OVER_PLAN = 1.5;
const R_MIN = 0.5;
const R_MAX = 1.5;
const R_WEEKS = 4;
const R_HOURS = 40;
const STALE_SHARE = 0.2;
const TCPI_GAP = 0.1;
/** r as it may be used — `null` without enough history or outside 0.5–1.5 (EV-4). */
function usableR(facts) {
    if (facts.rRaw === null || facts.rWeeks < R_WEEKS || facts.rHours < R_HOURS)
        return null;
    return facts.rRaw < R_MIN || facts.rRaw > R_MAX ? null : facts.rRaw;
}
function evaluateEac(facts) {
    const { bac, ev, ac, rem } = facts;
    const left = Math.max(0, bac - ev);
    const addend = facts.addend ?? 0;
    const cpi = ac > EPSILON ? ev / ac : null;
    const r = usableR(facts);
    const need = cpi !== null && cpi > EPSILON ? left / cpi : left > EPSILON ? left : null;
    /* ── trend ── */
    const trendWhy = [];
    if (bac > EPSILON && ev / bac < STABLE_SHARE)
        trendWhy.push('notStable');
    if (facts.unestimatedShare > UNESTIMATED_DISABLE)
        trendWhy.push('unestimated');
    if (ac <= EPSILON)
        trendWhy.push('noCost');
    if (facts.itemCount === 1 && (facts.maxItemPlanRatio ?? 0) > OVER_PLAN) {
        trendWhy.push('singleItemOverPlan');
    }
    /* ── schedule ── */
    const scheduleWhy = [];
    if (trendWhy.length > 0)
        scheduleWhy.push('trendDisabled');
    if (facts.planThin)
        scheduleWhy.push('planThin');
    if ((facts.maxItemPlanRatio ?? 0) > OVER_PLAN)
        scheduleWhy.push('itemOverPlan');
    /* ── resource ── */
    const resourceWhy = [];
    if (rem <= EPSILON && left > EPSILON)
        resourceWhy.push('allocationEnded');
    else if (need !== null && rem < need * 0.5)
        resourceWhy.push('underAllocated');
    if (facts.peopleWithoutSchedule > 0)
        resourceWhy.push('noSchedule');
    if (facts.unit === 'money' && facts.peopleWithoutCostRate > 0)
        resourceWhy.push('noCostRate');
    /* ── adjusted ── */
    const adjustedWhy = [];
    if (resourceWhy.length > 0)
        adjustedWhy.push('resourceDisabled');
    if (facts.rRaw === null || facts.rWeeks < R_WEEKS || facts.rHours < R_HOURS) {
        adjustedWhy.push('noHistory');
    }
    else if (r === null) {
        adjustedWhy.push('rOutOfRange');
    }
    const spiT = facts.spiT;
    const trendFigure = facts.fixed?.trend ?? (cpi !== null && cpi > EPSILON ? ac + left / cpi : null);
    const realistic = (figure, covered) => need !== null && covered < need - EPSILON && trendFigure !== null
        ? Math.max(figure, trendFigure)
        : figure;
    const value = (mode) => {
        switch (mode) {
            case 'budget':
                return facts.fixed?.budget ?? ac + left;
            case 'trend':
                return facts.fixed?.trend ?? (cpi !== null && cpi > EPSILON ? ac + left / cpi : null);
            case 'schedule':
                return cpi !== null && cpi > EPSILON && spiT !== null && spiT > EPSILON
                    ? ac + left / (cpi * spiT)
                    : null;
            /**
             * ⚠️ The summary, §1: an allocation short of what the remaining work needs at today's CPI
             * would forecast too little — the realistic figure is then the higher of it and Trendtartó.
             */
            case 'resource':
                return realistic(ac + rem, rem);
            case 'adjusted':
                return r === null ? null : realistic(ac + rem * r, rem * r);
        }
    };
    const why = {
        budget: [],
        trend: trendWhy,
        schedule: scheduleWhy,
        resource: resourceWhy,
        adjusted: adjustedWhy,
    };
    const modes = exports.EAC_MODES.map((mode) => {
        const raw = value(mode);
        const reasons = why[mode];
        return {
            mode,
            value: raw === null ? null : raw + addend,
            /* ⚠️ A mode with no figure is not enabled either, whatever the rules say — Alaprátás always
               has one, which is what makes it the last fallback. */
            enabled: reasons.length === 0 && raw !== null,
            reasons,
        };
    });
    const byMode = new Map(modes.map((one) => [one.mode, one]));
    const defaultMode = exports.EAC_DEFAULT_CHAIN.find((mode) => byMode.get(mode)?.enabled === true) ?? 'budget';
    /* ── the band (EV-6) ── */
    const low = byMode.get('budget')?.value ?? null;
    const schedule = byMode.get('schedule');
    const pessimistic = schedule?.enabled === true && schedule.value !== null
        ? schedule
        : modes
            .filter((one) => one.enabled && one.value !== null)
            .reduce((top, one) => (top === null || (one.value ?? 0) > (top.value ?? 0) ? one : top), null);
    const highValue = pessimistic?.value ?? null;
    const band = low === null || pessimistic === null || highValue === null
        ? null
        : { low, high: Math.max(low, highValue), highMode: pessimistic.mode };
    const shortOf = (available) => need === null ? null : Math.max(0, need - available);
    /* ── TCPI ── */
    const tcpi = ac > bac || Math.abs(bac - ac) <= EPSILON ? null : (bac - ev) / (bac - ac);
    /* ── the flags ── */
    const red = [];
    if (facts.unestimatedShare > RED_SHARE) {
        red.push({ flag: 'unestimatedHours', value: facts.unestimatedShare });
    }
    if (facts.unboundShare > RED_SHARE)
        red.push({ flag: 'unboundHours', value: facts.unboundShare });
    if (facts.itemsWithoutTasks > 0) {
        red.push({ flag: 'itemsWithoutTasks', value: facts.itemsWithoutTasks });
    }
    if (facts.reestimatedClosed > 0) {
        red.push({ flag: 'reestimatedClosed', value: facts.reestimatedClosed });
    }
    if (facts.rulesOnStatus > 0)
        red.push({ flag: 'rulesOnStatus', value: facts.rulesOnStatus });
    const yellow = [];
    if (facts.staleShare > STALE_SHARE) {
        yellow.push({ flag: 'staleAllocation', value: facts.staleShare });
    }
    if ((facts.maxItemPlanRatio ?? 0) > OVER_PLAN) {
        yellow.push({ flag: 'itemOverPlan', value: facts.maxItemPlanRatio ?? 0 });
    }
    if (facts.silentPeople > 0)
        yellow.push({ flag: 'silentPeople', value: facts.silentPeople });
    if (facts.unit === 'money' && facts.fxMissing)
        yellow.push({ flag: 'fxMissing', value: 1 });
    return {
        modes,
        defaultMode,
        band,
        need,
        shortResource: shortOf(rem),
        shortAdjusted: r === null ? null : shortOf(rem * r),
        r,
        cpi,
        spiT,
        tcpi,
        tcpiGap: tcpi !== null && cpi !== null && tcpi - cpi > TCPI_GAP,
        red,
        yellow,
    };
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Earned schedule — the working day the plan reached today's EV.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `planByWorkday` is the scope's planned value per working day from its start, in order. ES is
 * how many of them it took for the cumulative plan to reach `ev`, interpolated inside the day it
 * crossed. SPI(t) = ES ÷ elapsed working days — unlike EV ÷ PV it does not drift to 1 at the end.
 */
function earnedSchedule(planByWorkday, ev) {
    if (planByWorkday.length === 0)
        return null;
    let cumulative = 0;
    for (let index = 0; index < planByWorkday.length; index += 1) {
        const day = planByWorkday[index] ?? 0;
        if (cumulative + day >= ev - EPSILON) {
            return index + (day > EPSILON ? Math.max(0, ev - cumulative) / day : 0);
        }
        cumulative += day;
    }
    return planByWorkday.length;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ When the remaining work runs out, for one mode's ETC.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The ETC is consumed day by day by the allocation × r from tomorrow; where the allocation ends,
 * by the last four weeks' burn per working day; with no allocation at all, the planned duration ÷
 * SPI(t). `isWorkday` is the workspace's calendar — the reason this is never done in a browser.
 */
function forecastFinish(input) {
    const aheadByDay = new Map();
    for (const [day, cost] of input.remainingByDay)
        aheadByDay.set(day, { cost, revenue: 0, hours: 0 });
    return walkEtc({
        ...input,
        aheadByDay,
        burnRevenueRatio: 0,
        burnHoursRatio: 0,
        deadline: input.today,
    }).finishOn;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 31 Oct 2026 · EV-10 · ONE MODE'S ETC, LAID OUT DAY BY DAY.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE CHART'S FORECAST AND THE FINISH DATE ARE ONE WALK** — `forecastFinish` is this
 * function's last day, so the line cannot end anywhere but where the tile says the work runs out.
 * The spec's *Előrejelzett befejezés* row: the ETC is spent against the allocation × r from
 * tomorrow, then at the last four weeks' burn per working day, and with no allocation at all over
 * the planned duration ÷ SPI(t). Where a mode's ETC is less than the allocation ahead, the line
 * stops rising on the day it runs out — not spread thin to the allocation's end (decided 31 Oct).
 *
 * ⚠️ **Income moves with the hours.** An allocated day carries what its hours bill (a T&M item's
 * rate; nothing for a fixed price, whose income lands on its end date elsewhere), scaled by the
 * same share of the day the ETC consumed. Past the allocation, the recent burn earns at the recent
 * burn's own income ÷ cost (`burnRevenueRatio`).
 *
 * ⚠️ **A remainder nothing dates** — the allocation ran out, nothing was logged in four weeks, and
 * there is no schedule to stretch — lands on the later of the last day walked and the deadline,
 * never today or earlier, so the line still ends on the tile. `finishOn` stays `null` for it: the
 * chart has to put the money somewhere; the finish date does not have to invent a day.
 *
 * ⚠️ 2 Nov 2026 · **Hours move with the money too**, so the chart's forecast column rises with its
 * line. An allocated day carries its own hours, scaled like its income by the share consumed (and by
 * r on Korrigált, since the offer is). Everything else — the burn, the SPI(t) spread, the undated
 * remainder — is money without hours behind it, and is turned into hours at `burnHoursRatio`: hours
 * per unit of cost, the recent burn's where there is one (`hoursPerCostOf`).
 */
function walkEtc(input) {
    const byDay = new Map();
    const put = (day, cost, revenue, hours = cost * input.burnHoursRatio) => {
        const seen = byDay.get(day);
        byDay.set(day, {
            cost: (seen?.cost ?? 0) + cost,
            revenue: (seen?.revenue ?? 0) + revenue,
            hours: (seen?.hours ?? 0) + hours,
        });
    };
    let need = input.etc;
    if (need <= EPSILON)
        return { finishOn: null, byDay };
    const factor = input.r ?? 1;
    const days = [...input.aheadByDay.keys()].filter((day) => day > input.today).sort();
    let cursor = input.today;
    for (const day of days) {
        const flow = input.aheadByDay.get(day) ?? { cost: 0, revenue: 0, hours: 0 };
        const offered = flow.cost * factor;
        cursor = day;
        if (offered <= EPSILON)
            continue;
        const spent = Math.min(need, offered);
        const share = spent / offered;
        put(day, spent, flow.revenue * factor * share, flow.hours * factor * share);
        need -= spent;
        if (need <= EPSILON)
            return { finishOn: day, byDay };
    }
    if (input.burnPerWorkday > EPSILON) {
        let guard = 0;
        while (need > EPSILON && guard < 1100) {
            guard += 1;
            cursor = nextDay(cursor);
            if (!input.isWorkday(cursor))
                continue;
            const spent = Math.min(need, input.burnPerWorkday);
            put(cursor, spent, spent * input.burnRevenueRatio);
            need -= spent;
        }
        if (need > EPSILON)
            put(cursor, need, need * input.burnRevenueRatio);
        return { finishOn: cursor, byDay };
    }
    if (days.length === 0 &&
        input.spiT !== null &&
        input.spiT > EPSILON &&
        input.plannedWorkdays > 0) {
        const finishOn = walkWorkdays(input.start, Math.ceil(input.plannedWorkdays / input.spiT), input.isWorkday);
        /* Spread evenly over the working days left before it; a finish already behind us takes the
           whole remainder on the next working day. */
        const left = [];
        let day = input.today;
        let guard = 0;
        while (day < finishOn && guard < 1100) {
            guard += 1;
            day = nextDay(day);
            if (input.isWorkday(day))
                left.push(day);
        }
        const spread = left.length > 0 ? left : [walkWorkdays(input.today, 1, input.isWorkday)];
        for (const one of spread) {
            const spent = need / spread.length;
            put(one, spent, spent * input.burnRevenueRatio);
        }
        return { finishOn, byDay };
    }
    const late = cursor > input.deadline ? cursor : input.deadline;
    put(late > input.today ? late : nextDay(input.today), need, need * input.burnRevenueRatio);
    return { finishOn: null, byDay };
}
/** `count` working days after `start`. ⚠️ Bounded: three years of days is past any contract. */
function walkWorkdays(start, count, isWorkday) {
    let left = count;
    let cursor = start;
    let guard = 0;
    while (left > 0 && guard < 1100) {
        guard += 1;
        cursor = nextDay(cursor);
        if (isWorkday(cursor))
            left -= 1;
    }
    return cursor;
}
function nextDay(day) {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString().slice(0, 10);
}
/* ── the wire ───────────────────────────────────────────────────────────── */
const figure = zod_1.z.string().regex(/^-?\d+(\.\d+)?$/u);
exports.eacModeResultSchema = zod_1.z.object({
    mode: zod_1.z.enum(exports.EAC_MODES),
    value: figure.nullable(),
    enabled: zod_1.z.boolean(),
    reasons: zod_1.z.array(zod_1.z.enum(exports.EAC_REASONS)),
    /** When the remaining work runs out under this mode's ETC, and how late against the deadline. */
    finishOn: zod_1.z.string().nullable(),
    slipWorkdays: zod_1.z.number().int().nullable(),
});
/**
 * ⚠️⚠️ ADDED UNDER RULE 8 (EV-1…EV-7) — the evaluation, on both dashboard responses. Figures in
 * the response's own unit, as strings like every other figure on the wire.
 */
exports.eacEvaluationSchema = zod_1.z.object({
    unit: zod_1.z.enum(['hours', 'money']),
    modes: zod_1.z.array(exports.eacModeResultSchema),
    defaultMode: zod_1.z.enum(exports.EAC_MODES),
    band: zod_1.z.object({ low: figure, high: figure, highMode: zod_1.z.enum(exports.EAC_MODES) }).nullable(),
    need: figure.nullable(),
    shortResource: figure.nullable(),
    shortAdjusted: figure.nullable(),
    r: zod_1.z.number().nullable(),
    rRaw: zod_1.z.number().nullable(),
    rWeeks: zod_1.z.number(),
    rHours: zod_1.z.number(),
    cpi: zod_1.z.number().nullable(),
    spiT: zod_1.z.number().nullable(),
    tcpi: zod_1.z.number().nullable(),
    tcpiGap: zod_1.z.boolean(),
    red: zod_1.z.array(zod_1.z.object({ flag: zod_1.z.enum(exports.EAC_RED_FLAGS), value: zod_1.z.number() })),
    yellow: zod_1.z.array(zod_1.z.object({ flag: zod_1.z.enum(exports.EAC_YELLOW_FLAGS), value: zod_1.z.number() })),
});
/** The evaluation on the wire, each mode carrying the finish the caller dated for it. */
function eacWire(facts, evaluation, finishOf) {
    const f = (value) => value.toFixed(2);
    return {
        unit: facts.unit,
        modes: evaluation.modes.map((one) => ({
            mode: one.mode,
            value: one.value === null ? null : f(one.value),
            enabled: one.enabled,
            reasons: [...one.reasons],
            ...finishOf(one.mode, one.value),
        })),
        defaultMode: evaluation.defaultMode,
        band: evaluation.band === null
            ? null
            : {
                low: f(evaluation.band.low),
                high: f(evaluation.band.high),
                highMode: evaluation.band.highMode,
            },
        need: evaluation.need === null ? null : f(evaluation.need),
        shortResource: evaluation.shortResource === null ? null : f(evaluation.shortResource),
        shortAdjusted: evaluation.shortAdjusted === null ? null : f(evaluation.shortAdjusted),
        r: evaluation.r,
        rRaw: facts.rRaw,
        rWeeks: facts.rWeeks,
        rHours: facts.rHours,
        cpi: evaluation.cpi,
        spiT: evaluation.spiT,
        tcpi: evaluation.tcpi,
        tcpiGap: evaluation.tcpiGap,
        red: [...evaluation.red],
        yellow: [...evaluation.yellow],
    };
}
//# sourceMappingURL=eac.js.map